import { Link } from 'react-router-dom';
import { PersonAvatar } from '../PersonAvatar';
import { Badge } from '../ui';
import type { DirectoryBadge, DirectoryEntry } from './types';

// Avatar: an authenticated PersonAvatar when the entry is backed by a person
// (principals), otherwise a monogram tile — a rounded square for businesses, a
// circle for individual clients.
function EntryAvatar({
  entry,
  size,
}: {
  entry: DirectoryEntry;
  size: 'sm' | 'md';
}): JSX.Element {
  if (entry.personId) {
    return (
      <PersonAvatar
        personId={entry.personId}
        photoPath={entry.photoPath}
        fullName={entry.name}
        size={size}
      />
    );
  }
  const round = entry.kind === 'client-individual';
  return (
    <span
      className={`dir-monogram dir-monogram--${size} ${round ? 'dir-monogram--round' : ''}`}
      aria-hidden="true"
      title={entry.name}
    >
      {entry.initials}
    </span>
  );
}

function BadgeBits({ badges }: { badges: DirectoryBadge[] }): JSX.Element | null {
  if (badges.length === 0) return null;
  return (
    <div className="dir-badges">
      {badges.map((b, i) =>
        b.className ? (
          <span key={`${b.label}-${i}`} className={`badge ${b.className}`}>
            {b.label}
          </span>
        ) : (
          <Badge key={`${b.label}-${i}`} tone={b.tone}>
            {b.label}
          </Badge>
        ),
      )}
    </div>
  );
}

export function DirectoryCard({ entry }: { entry: DirectoryEntry }): JSX.Element {
  return (
    <Link to={entry.to} className="dir-card">
      <div className="dir-card-top">
        <EntryAvatar entry={entry} size="md" />
        <BadgeBits badges={entry.badges} />
      </div>
      <div className="dir-card-name">{entry.name}</div>
      {entry.subtitle && <div className="dir-card-sub">{entry.subtitle}</div>}
      {entry.meta.length > 0 && (
        <dl className="dir-meta">
          {entry.meta.map((m) => (
            <div key={m.label} className="dir-meta-row">
              <dt>{m.label}</dt>
              <dd>{m.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Link>
  );
}

export function DirectoryRow({ entry }: { entry: DirectoryEntry }): JSX.Element {
  return (
    <Link to={entry.to} className="dir-row">
      <EntryAvatar entry={entry} size="sm" />
      <div className="dir-row-main">
        <span className="dir-row-name">{entry.name}</span>
        {entry.subtitle && <span className="dir-row-sub">{entry.subtitle}</span>}
      </div>
      <div className="dir-row-meta">
        {entry.meta.map((m) => (
          <span key={m.label} className="dir-row-metaitem">
            <span className="dir-row-metalabel">{m.label}</span>
            {m.value}
          </span>
        ))}
      </div>
      <BadgeBits badges={entry.badges} />
    </Link>
  );
}
